import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Box, Typography, List, ListItem, ListItemText, CircularProgress, Button,
    Snackbar, Alert, Card, CardContent, Grid, Accordion, AccordionSummary,
    AccordionDetails, Chip, Paper, Stack, FormControl, InputLabel, Select,
    MenuItem, TextField, TableContainer, Table, TableHead, TableRow, TableCell,
    TableBody, TableSortLabel, Tooltip // Added Tooltip
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import LaunchIcon from '@mui/icons-material/Launch'; // Icon for links
import api from './api'; // Assuming api setup handles token
import config from './config.js';
import { isTokenExpired } from './auth';

// --- Sorting Helper Functions (Unchanged) ---
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator(a, b, orderBy) {
  if (orderBy === 'completed_status') {
      const valA = a[orderBy] ? 1 : 0;
      const valB = b[orderBy] ? 1 : 0;
      if (valB < valA) return -1;
      if (valB > valA) return 1;
      return 0;
  }
  let valA = String(a[orderBy] ?? '').toLowerCase();
  let valB = String(b[orderBy] ?? '').toLowerCase();
  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}
// --- End Sorting Helpers ---

function ReportsLanding(props) {
    const { userRole: userRoleProp, userId: userIdProp } = props;
    console.log("ReportsLanding received props:", props);

    // --- State variables ---
    const [reportsData, setReportsData] = useState(null); // Holds raw data (structure depends on role)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cohortFetchError, setCohortFetchError] = useState(null);
    const [downloadError, setDownloadError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [allCohorts, setAllCohorts] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [selectedCohortId, setSelectedCohortId] = useState('');
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [surveyRoundsForCompletion, setSurveyRoundsForCompletion] = useState([]);
    const [selectedCompletionCohortId, setSelectedCompletionCohortId] = useState('');
    const [selectedSurveyIdForCompletion, setSelectedSurveyIdForCompletion] = useState('');
    const [completionData, setCompletionData] = useState([]);
    const [completionLoading, setCompletionLoading] = useState(false);
    const [completionError, setCompletionError] = useState(null);
    const [completionFilterText, setCompletionFilterText] = useState("");
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('user_name');

    const currentUserRoleNum = useMemo(() => userRoleProp ? Number(userRoleProp) : null, [userRoleProp]);

    // --- Fetch initial data ---
    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem('token');
            if (!token || isTokenExpired()) { setError("Session expired or token missing. Please log in."); setLoading(false); return; }
            if (userIdProp === null || currentUserRoleNum === null) { console.log("ReportsLanding: Waiting for userId/userRole props..."); return; }

            setLoading(true); setError(null); setCohortFetchError(null); setCompletionError(null);

            try {
                // Determine the API endpoint based on role
                let reportUrl;
                if (currentUserRoleNum === 1 || currentUserRoleNum === 2) {
                    reportUrl = `${config.backendUrl}/reports`; // Fetches all, backend restructures
                } else {
                    reportUrl = `${config.backendUrl}/reports?user_id=${userIdProp}`; // Fetches specific user's data
                }
                console.log(`ReportsLanding: Fetching reports from ${reportUrl}`);

                const reportsPromise = api.get(reportUrl);
                const roundsPromise = api.get(`${config.backendUrl}/survey_rounds`);
                const cohortPromise = (currentUserRoleNum === 1 || currentUserRoleNum === 2) ? api.get(`${config.backendUrl}/cohorts`) : Promise.resolve({ data: [] });
                const teamPromise = (currentUserRoleNum === 1 || currentUserRoleNum === 2) ? api.get(`${config.backendUrl}/teams`) : Promise.resolve({ data: [] });

                const [reportsResult, roundsResult, cohortsResult, teamsResult] = await Promise.allSettled([reportsPromise, roundsPromise, cohortPromise, teamPromise]);

                // Process Reports
                if (reportsResult.status === 'fulfilled') {
                    const fetchedData = reportsResult.value.data;
                    console.log("ReportsLanding: Raw reports data received:", fetchedData);
                    if (Array.isArray(fetchedData)) {
                        setReportsData(fetchedData);
                    } else {
                        throw new Error("Invalid report data format received from backend.");
                    }
                } else {
                    console.error("ReportsLanding: Failed to fetch reports:", reportsResult.reason);
                    setError(reportsResult.reason?.response?.data?.message || reportsResult.reason?.message || "Failed to load report data.");
                    setReportsData([]);
                }

                // Process Rounds
                if (roundsResult.status === 'fulfilled') {
                    const roundsData = roundsResult.value.data;
                    if (!Array.isArray(roundsData)) throw new Error("Invalid survey round data format");
                    const validRounds = roundsData
                        .filter(r => r && r.survey_id != null && r.survey_name != null)
                        .sort((a, b) => new Date(b.end_date || 0) - new Date(a.end_date || 0));
                    setSurveyRoundsForCompletion(validRounds);
                } else {
                    console.error("ReportsLanding: Failed to fetch survey rounds:", roundsResult.reason);
                    setError(prev => prev ? `${prev}, Failed to load survey rounds.` : "Failed to load survey rounds.");
                    setSurveyRoundsForCompletion([]);
                }

                // Process Cohorts
                if (cohortsResult.status === 'fulfilled') {
                    const cohortsData = cohortsResult.value.data;
                    if (!Array.isArray(cohortsData)) throw new Error("Invalid cohort data format");
                    const validCohorts = cohortsData
                        .filter(c => c && c.cohort_id != null && c.cohort_name != null)
                        .sort((a, b) => a.cohort_name.localeCompare(b.cohort_name));
                    setAllCohorts(validCohorts);
                } else if (cohortsResult.status === 'rejected') {
                    console.error("ReportsLanding: Failed to fetch cohorts:", cohortsResult.reason);
                    setCohortFetchError("Failed to load cohort filter options.");
                    setAllCohorts([]);
                } else {
                    setAllCohorts([]);
                }

                 // Process Teams
                 if (teamsResult.status === 'fulfilled') {
                    const teamsData = teamsResult.value.data;
                    if (!Array.isArray(teamsData)) throw new Error("Invalid team data format");
                    const validTeams = teamsData
                        .filter(t => t && t.team_id != null && t.team_name != null)
                        .sort((a, b) => a.team_name.localeCompare(b.team_name));
                    setAllTeams(validTeams);
                } else if (teamsResult.status === 'rejected') {
                    console.error("ReportsLanding: Failed to fetch teams:", teamsResult.reason);
                    setError(prev => prev ? `${prev}, Failed to load team filter options.` : "Failed to load team filter options.");
                    setAllTeams([]);
                } else {
                     setAllTeams([]);
                }

            } catch (err) {
                console.error("ReportsLanding: Error during initial data fetch:", err);
                setError(err.message || "Failed to process initial data.");
                setReportsData([]); setAllCohorts([]); setAllTeams([]); setSurveyRoundsForCompletion([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [userIdProp, currentUserRoleNum]);

    // --- Filtered Data for Display ---
    const displayData = useMemo(() => {
        console.log("ReportsLanding: Recalculating displayData...");
        if (!reportsData) return [];

        let dataToProcess = reportsData;

        // Apply Filters if selected (Only for Admin/Mod view)
        if (currentUserRoleNum === 1 || currentUserRoleNum === 2) {
            if (selectedCohortId) {
                dataToProcess = dataToProcess.filter(cohort => Number(cohort.cohort_id) === Number(selectedCohortId));
            }
            if (selectedTeamId) {
                dataToProcess = dataToProcess.map(cohort => ({
                    ...cohort,
                    teams: cohort.teams.filter(team => Number(team.team_id) === Number(selectedTeamId))
                })).filter(cohort => cohort.teams.length > 0);
            }
        }
        console.log("ReportsLanding: Data count after filtering:", dataToProcess.length);
        return dataToProcess;
    }, [reportsData, currentUserRoleNum, selectedCohortId, selectedTeamId]);

    // Filter teams for the main report filter dropdown
    const filteredTeamsForDropdown = useMemo(() => {
        if (!selectedCohortId) return allTeams;
        return allTeams.filter(team => Number(team.cohort_id) === Number(selectedCohortId));
    }, [selectedCohortId, allTeams]);


    // --- Download Handlers ---
    const handleDownload = useCallback(async (url, filenamePrefix) => {
        setDownloadError(null); setIsDownloading(true);
        try {
            const params = new URLSearchParams();
            if (selectedCohortId) params.append('cohort_id', selectedCohortId);
            if (selectedTeamId) params.append('team_id', selectedTeamId);
            const fullUrl = `${url}?${params.toString()}`;
            console.log(`ReportsLanding: Downloading from ${fullUrl}`);
            const response = await api.get(fullUrl, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement('a'); link.href = blobUrl;
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-T:]/g, "");
            link.setAttribute('download', `${filenamePrefix}_${timestamp}.csv`);
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
             console.error(`Error downloading ${filenamePrefix}:`, err);
             let message = `An error occurred while downloading the ${filenamePrefix} report.`;
             if (err.response && err.response.data && err.response.data instanceof Blob) {
                 try { const errorText = await err.response.data.text(); const errorJson = JSON.parse(errorText); message = errorJson.message || errorJson.error || message; }
                 catch (parseError) { console.error("Could not parse error response:", parseError); }
             } else if (err.response?.data?.message) { message = err.response.data.message; }
             else if (err.message) { message = err.message; }
             setDownloadError(message);
        } finally { setIsDownloading(false); }
    }, [selectedCohortId, selectedTeamId]);

    const downloadAllCsv = useCallback(() => handleDownload(`${config.backendUrl}/reports/download`, 'reports'), [handleDownload]);
    const downloadQualitativeCsv = useCallback(() => handleDownload(`${config.backendUrl}/reports/download/qualitative`, 'qualitative_feedback'), [handleDownload]);

    // **FIX:** Specific handler for Completion Status CSV download using api.get
    const downloadCompletionCsv = useCallback(async () => {
        if (!selectedSurveyIdForCompletion || !selectedCompletionCohortId) {
            setDownloadError("Please select cohort and survey round first.");
            return;
        }
        setDownloadError(null); setIsDownloading(true);
        try {
            const params = new URLSearchParams({
                survey_id: selectedSurveyIdForCompletion,
                cohort_id: selectedCompletionCohortId,
                format: 'csv' // Request CSV format
            });
            const url = `${config.backendUrl}/api/survey-completion-status?${params.toString()}`;
            console.log(`ReportsLanding: Downloading Completion Status from ${url}`);
            // Use api.get to include auth header and request blob response
            const response = await api.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
            const link = document.createElement('a'); link.href = blobUrl;
            const filename = `completion_status_s${selectedSurveyIdForCompletion}_c${selectedCompletionCohortId}.csv`;
            link.setAttribute('download', filename);
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error("Error downloading completion status CSV:", err);
            let message = "An error occurred while downloading the completion status report.";
            if (err.response && err.response.data && err.response.data instanceof Blob) {
                 try { const errorText = await err.response.data.text(); const errorJson = JSON.parse(errorText); message = errorJson.message || errorJson.error || message; }
                 catch (parseError) { console.error("Could not parse error response:", parseError); }
            } else if (err.response?.data?.message) { message = err.response.data.message; }
            else if (err.message) { message = err.message; }
            setDownloadError(message);
        } finally { setIsDownloading(false); }
    }, [selectedCompletionCohortId, selectedSurveyIdForCompletion]); // Dependencies

    const handleCloseDownloadError = () => setDownloadError(null);

    // --- Filter Handlers ---
    const handleCohortFilterChange = (event) => { setSelectedCohortId(event.target.value); setSelectedTeamId(''); };
    const handleTeamFilterChange = (event) => { setSelectedTeamId(event.target.value); };
    const clearFilters = () => { setSelectedCohortId(''); setSelectedTeamId(''); };

    // --- Completion Status Logic ---
    const fetchCompletionStatus = useCallback(async () => {
        if (!selectedSurveyIdForCompletion || !selectedCompletionCohortId) { setCompletionError("Please select both a cohort and a survey round."); setCompletionData([]); return; }
        setCompletionLoading(true); setCompletionError(null); setCompletionData([]);
         try {
            // Fetch JSON for table display
            let url = `${config.backendUrl}/api/survey-completion-status?survey_id=${selectedSurveyIdForCompletion}&cohort_id=${selectedCompletionCohortId}`;
            const response = await api.get(url);
            const data = response.data;
            if (!Array.isArray(data)) throw new Error("Invalid completion data format");
            setCompletionData(data);
         } catch (err) {
             console.error("ReportsLanding: Error fetching completion status:", err);
             let message = "Failed to fetch completion status.";
             if (err.response && err.response.data && err.response.data.message) { message = err.response.data.message; }
             else if (err.message) { message = err.message; }
             setCompletionError(message); setCompletionData([]);
         } finally { setCompletionLoading(false); }
    }, [selectedSurveyIdForCompletion, selectedCompletionCohortId]);

    const handleCompletionCohortChange = (event) => { setSelectedCompletionCohortId(event.target.value); setCompletionData([]); setCompletionError(null); };
    const handleSurveyForCompletionChange = (event) => { setSelectedSurveyIdForCompletion(event.target.value); setCompletionData([]); setCompletionError(null); };
    const handleCompletionFilterChange = (event) => { setCompletionFilterText(event.target.value); };
    const handleRequestSort = (property) => { const isAsc = orderBy === property && order === 'asc'; setOrder(isAsc ? 'desc' : 'asc'); setOrderBy(property); };
    const visibleCompletionData = useMemo(() => {
        let filtered = completionData;
        if (completionFilterText) {
            const lowerCaseFilter = completionFilterText.toLowerCase();
            filtered = completionData.filter(item =>
                String(item.user_name ?? '').toLowerCase().includes(lowerCaseFilter) ||
                String(item.user_email ?? '').toLowerCase().includes(lowerCaseFilter) ||
                String(item.team_name ?? '').toLowerCase().includes(lowerCaseFilter)
            );
        }
        return stableSort(filtered, getComparator(order, orderBy));
    }, [completionData, completionFilterText, order, orderBy]);

    // --- Render ---
    if (loading) { return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>; }

    const hasVisibleReports = displayData && displayData.length > 0;

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, maxWidth: '1200px', margin: 'auto' }}>
            <Typography variant="h4" gutterBottom component="h1" sx={{ textAlign: 'center', mb: 3 }}> Available Reports </Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {cohortFetchError && <Alert severity="warning" sx={{ mb: 2 }}>{cohortFetchError}</Alert>}

            {/* Admin/Moderator Filter Bar */}
            {(currentUserRoleNum === 1 || currentUserRoleNum === 2) && (
                <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                     <Grid container spacing={2} alignItems="center">
                         <Grid item xs={12} sm={3}> <FormControl fullWidth size="small"> <InputLabel id="cohort-filter-label">Filter by Cohort</InputLabel> <Select labelId="cohort-filter-label" value={selectedCohortId} label="Filter by Cohort" onChange={handleCohortFilterChange} > <MenuItem value=""><em>All Cohorts</em></MenuItem> {allCohorts.map((cohort) => ( <MenuItem key={cohort.cohort_id} value={cohort.cohort_id}> {cohort.cohort_name} </MenuItem> ))} </Select> </FormControl> </Grid>
                         <Grid item xs={12} sm={3}> <FormControl fullWidth size="small"> <InputLabel id="team-filter-label">Filter by Team</InputLabel> <Select labelId="team-filter-label" value={selectedTeamId} label="Filter by Team" onChange={handleTeamFilterChange} > <MenuItem value=""><em>All Teams</em></MenuItem> {filteredTeamsForDropdown.map((team) => ( <MenuItem key={team.team_id} value={team.team_id}> {team.team_name} </MenuItem> ))} </Select> </FormControl> </Grid>
                         <Grid item xs={12} sm={2}> <Button variant="text" size="small" onClick={clearFilters} startIcon={<ClearIcon />} disabled={!selectedCohortId && !selectedTeamId} > Clear Filters </Button> </Grid>
                         <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right'} }}> <Stack direction="row" spacing={1} justifyContent="flex-end"> <Button variant="outlined" color="primary" size="small" onClick={downloadQualitativeCsv} startIcon={<ChatBubbleOutlineIcon />} disabled={isDownloading} title="Download Qualitative Feedback" > {isDownloading ? '...' : 'Qualitative'} </Button> <Button variant="contained" color="secondary" size="small" onClick={downloadAllCsv} startIcon={<DownloadIcon />} disabled={isDownloading} title="Download All Report Data" > {isDownloading ? '...' : 'All Data'} </Button> </Stack> </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Report Display Area */}
            { !loading && !hasVisibleReports ? (
                 <Paper elevation={2} sx={{ p: 3, textAlign: 'center', mb: 4 }}>
                     <Typography variant="h6">
                         {currentUserRoleNum === 3 ? "No completed reports available." : "No reports match the current filters or none available."}
                     </Typography>
                 </Paper>
            ) : (
                <Box mb={4}>
                    {/* Admin/Moderator Grouped View */}
                    {(currentUserRoleNum === 1 || currentUserRoleNum === 2) && (
                        <Box>
                            {displayData.map((cohort) => (
                                <Card variant="outlined" sx={{ mb: 3 }} key={cohort.cohort_id}>
                                    <CardContent sx={{ pb: '8px !important' }}>
                                        <Typography variant="h5" component="div" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                            <Chip label="Cohort" size="small" sx={{ mr: 1 }} color="primary" /> {cohort.cohort_name}
                                        </Typography>
                                        {cohort.teams.map((team) => (
                                            <Accordion key={team.team_id} sx={{ mt: 1, '&:before': { display: 'none' }, boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.12)', '&.Mui-expanded': { mt: 1, mb: 1 } }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`panel-${team.team_id}-content`} id={`panel-${team.team_id}-header`} sx={{ minHeight: '48px', '&.Mui-expanded': { minHeight: '48px' }, backgroundColor: 'rgba(0, 0, 0, .02)' }}>
                                                    <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
                                                        <Chip label="Team" size="small" sx={{ mr: 1 }} color="secondary" /> {team.team_name}
                                                    </Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, borderTop: '1px solid rgba(0, 0, 0, 0.12)' }}>
                                                    {team.rounds.map((round) => (
                                                        <Box key={round.survey_id} sx={{ borderBottom: '1px solid #eee', '&:last-child': { borderBottom: 0 } }}>
                                                            <ListItem sx={{ backgroundColor: 'rgba(0, 0, 0, .01)', py: 0.5 }}>
                                                                <ListItemText
                                                                    primary={`Round: ${round.survey_name}`}
                                                                    secondary={`Ended: ${round.end_date ? new Date(round.end_date).toLocaleDateString() : 'N/A'}`}
                                                                    primaryTypographyProps={{ fontWeight: 'medium', fontSize: '1rem' }}
                                                                    secondaryTypographyProps={{ fontSize: '0.8rem' }}
                                                                />
                                                            </ListItem>
                                                            <List dense disablePadding>
                                                                {round.users.map((user) => (
                                                                    <ListItem key={`${round.survey_id}-${user.user_id}`} button component={Link} to={user.report_link} sx={{ pl: 4, py: 0.5 }}>
                                                                        <ListItemText primary={user.user_name} />
                                                                        <LaunchIcon fontSize="small" color="action" sx={{ ml: 1 }} />
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>
                                                    ))}
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                        {cohort.teams.length === 0 && <Typography sx={{ fontStyle: 'italic', color: 'text.secondary', mt: 1 }}>No teams match the current filter in this cohort.</Typography>}
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}

                    {/* Regular User View (Role 3) */}
                    {currentUserRoleNum === 3 && (
                         <Grid container spacing={3}>
                            {(displayData[0]?.rounds ?? []).map((round) => {
                                const roundInfo = round.round_info;
                                if (!roundInfo) return null;
                                let formattedDate = 'N/A';
                                try {
                                    if (roundInfo.end_date) {
                                        const dateObj = new Date(roundInfo.end_date);
                                        if (!isNaN(dateObj.getTime())) {
                                             formattedDate = dateObj.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
                                        }
                                    }
                                } catch (e) { console.error("Error formatting report date", e); }
                                const reportLink = `/report/${roundInfo.survey_id}/${userIdProp}`;
                                return (
                                    <Grid item xs={12} sm={6} md={4} key={roundInfo.survey_id}>
                                        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" gutterBottom component="div">
                                                    {roundInfo.survey_name || `Survey ${roundInfo.survey_id}`}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Survey Ended: {formattedDate}
                                                </Typography>
                                            </CardContent>
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <Button component={Link} to={reportLink} variant="contained" size="small" fullWidth startIcon={<LaunchIcon />} > View My Report </Button>
                                            </Box>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                </Box>
            )}
            {/* --- End Report Display Section --- */}


            {/* --- Survey Completion Status Section (Admin/Mod Only) --- */}
            {(currentUserRoleNum === 1 || currentUserRoleNum === 2) && (
                <Paper elevation={1} sx={{ p: 2, mb: 4, mt: 4 }}>
                    <Typography variant="h6" gutterBottom>Survey Completion Status</Typography>
                    {completionError && <Alert severity="error" sx={{ mb: 2 }}>{completionError}</Alert>}
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={4}> <FormControl fullWidth size="small"> <InputLabel id="completion-cohort-filter-label">Cohort</InputLabel> <Select labelId="completion-cohort-filter-label" value={selectedCompletionCohortId} label="Cohort" onChange={handleCompletionCohortChange} > <MenuItem value=""><em>Select Cohort</em></MenuItem> {allCohorts.map((cohort) => ( <MenuItem key={cohort.cohort_id} value={cohort.cohort_id}> {cohort.cohort_name} </MenuItem> ))} </Select> </FormControl> </Grid>
                         <Grid item xs={12} sm={4}> <FormControl fullWidth size="small"> <InputLabel id="completion-survey-filter-label">Survey Round</InputLabel> <Select labelId="completion-survey-filter-label" value={selectedSurveyIdForCompletion} label="Survey Round" onChange={handleSurveyForCompletionChange} > <MenuItem value=""><em>Select Survey</em></MenuItem> {surveyRoundsForCompletion.map((round) => ( <MenuItem key={round.survey_id} value={round.survey_id}> {round.survey_name || `Round ID: ${round.survey_id}`} </MenuItem> ))} </Select> </FormControl> </Grid>
                         <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}> <Button variant="contained" onClick={fetchCompletionStatus} disabled={!selectedCompletionCohortId || !selectedSurveyIdForCompletion || completionLoading} fullWidth > {completionLoading ? <CircularProgress size={24} /> : "Fetch Status"} </Button> </Grid>
                    </Grid>

                    {/* Completion Table Area */}
                    {!completionLoading && (completionData.length > 0 || (selectedCompletionCohortId && selectedSurveyIdForCompletion && !completionError)) && (
                        <>
                            <TextField label="Filter Results (by Name, Email, Team)" variant="outlined" fullWidth size="small" value={completionFilterText} onChange={handleCompletionFilterChange} sx={{ my: 2 }} />
                            <TableContainer component={Paper} sx={{ mt: 1, maxHeight: 440 }}>
                                <Table stickyHeader size="small" aria-label="completion status table">
                                    <TableHead>
                                        <TableRow>
                                            {[ { id: 'user_name', label: 'User Name' }, { id: 'user_email', label: 'Email' }, { id: 'team_name', label: 'Team' }, { id: 'completed_status', label: 'Status' }, ].map((headCell) => (
                                                <TableCell key={headCell.id} sortDirection={orderBy === headCell.id ? order : false} sx={{ bgcolor: 'background.paper' }}>
                                                    <TableSortLabel active={orderBy === headCell.id} direction={orderBy === headCell.id ? order : 'asc'} onClick={() => handleRequestSort(headCell.id)} >
                                                        {headCell.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {visibleCompletionData.map((row) => (
                                            <TableRow key={row.user_id} hover>
                                                <TableCell>{row.user_name}</TableCell>
                                                <TableCell>{row.user_email}</TableCell>
                                                <TableCell>{row.team_name}</TableCell>
                                                <TableCell> <Chip label={row.completed_status ? "Completed" : "Not Completed"} color={row.completed_status ? "success" : "error"} size="small" variant="outlined" /> </TableCell>
                                            </TableRow>
                                        ))}
                                         {visibleCompletionData.length === 0 && ( <TableRow> <TableCell colSpan={4} align="center"> {completionFilterText ? "No results match your filter." : "No completion data found for selected filters."} </TableCell> </TableRow> )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                             {/* **FIX:** Use Button with onClick handler for completion download */}
                             {completionData.length > 0 && (
                                <Button
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                    onClick={downloadCompletionCsv} // Use the correct handler
                                    disabled={isDownloading}
                                    startIcon={<DownloadIcon />}
                                >
                                    {isDownloading ? 'Downloading...' : 'Download Completion CSV'}
                                </Button>
                             )}
                        </>
                    )}
                     {!completionLoading && completionData.length === 0 && selectedCompletionCohortId && selectedSurveyIdForCompletion && !completionError && ( <Typography sx={{ mt: 2, textAlign: 'center' }}>No users found for the selected cohort and survey.</Typography> )}
                </Paper>
            )}
            {/* --- End Completion Status Section --- */}

             {/* Snackbar for Download Errors */}
             <Snackbar open={!!downloadError} autoHideDuration={6000} onClose={handleCloseDownloadError} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseDownloadError} severity="error" variant="filled" sx={{ width: '100%' }}> {downloadError} </Alert>
            </Snackbar>
        </Box>
    );
}

export default ReportsLanding;
